import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Plus, Trash2, Sparkles, Check, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CAUSES, DISTRICTS, CREDENTIAL_TYPES, BUDGET_BANDS } from "@/lib/causes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import MediaUploader from "@/components/MediaUploader";

const REG_TYPES = ["Trust", "Society", "Section 8 Company"];
const STEPS = ["Basics", "Registration", "Credentials", "Projects & causes", "Contact & visit", "Review"];

export default function NGODashboard() {
  const { user } = useAuth();
  const [existing, setExisting] = useState(undefined);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [form, setForm] = useState({
    legal_name: "", display_name: "", reg_type: "Trust", incorporation_date: "", darpan_id: "",
    mission: "", mission_ta: "", story: "", district_id: "erode", pincode: "",
    human_cause_tags: [], hero_image: "", accepts_volunteers: false, budget_band: "5l_25l",
    beneficiary_count: 0,
    contact: { person: "", phone: "", email: "", visiting_hours: "Mon–Sat, 10:00–16:00", address: "", donation_url: "" },
    credentials: [{ type: "DARPAN", number: "", expiry_date: "" }],
  });

  useEffect(() => {
    api.get("/ngo/org").then((r) => {
      setExisting(r.data.org);
      if (r.data.org) {
        const o = r.data.org;
        setForm((f) => ({ ...f, ...o, contact: o.contact || f.contact, credentials: (o.credentials || []).map((c) => ({ type: c.type, number: c.number, expiry_date: c.expiry_date || "" })) || f.credentials }));
      }
    }).catch(() => setExisting(null));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setContact = (k, v) => setForm((f) => ({ ...f, contact: { ...f.contact, [k]: v } }));
  const toggleCause = (id) => setForm((f) => ({ ...f, human_cause_tags: f.human_cause_tags.includes(id) ? f.human_cause_tags.filter((x) => x !== id) : [...f.human_cause_tags, id] }));
  const addCred = () => setForm((f) => ({ ...f, credentials: [...f.credentials, { type: "80G", number: "", expiry_date: "" }] }));
  const setCred = (i, k, v) => setForm((f) => { const c = [...f.credentials]; c[i] = { ...c[i], [k]: v }; return { ...f, credentials: c }; });
  const rmCred = (i) => setForm((f) => ({ ...f, credentials: f.credentials.filter((_, x) => x !== i) }));

  const strength = (() => {
    let s = 0;
    if (form.legal_name && form.display_name) s += 20;
    if (form.reg_type && form.incorporation_date) s += 15;
    if (form.darpan_id) s += 10;
    if (form.credentials.filter((c) => c.number).length >= 2) s += 20;
    if (form.human_cause_tags.length > 0) s += 15;
    if (form.mission) s += 10;
    if (form.contact.person && form.contact.phone) s += 10;
    return s;
  })();

  const aiStory = async () => {
    if (!form.display_name) { toast.error("Add your organisation name first"); return; }
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/story-draft", { title: form.display_name, notes: form.mission || form.human_cause_tags.join(", ") });
      set("story", data.draft);
      toast.success("Draft generated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setAiLoading(false); }
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/ngo/org", form);
      toast.success("Submitted for verification");
      const r = await api.get("/ngo/org");
      setExisting(r.data.org);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  if (existing === undefined) return <div className="min-h-[60vh] flex items-center justify-center text-stone-500">Loading…</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-10">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-brand">NGO dashboard</h1>
          <p className="text-sm text-stone-500">{user?.org_name || form.display_name || "Set up your free profile"}</p>
        </div>
        {existing && (
          <div className="flex items-center gap-2 bg-white border border-stone-200 rounded-lg px-4 py-2" data-testid="profile-views">
            <Eye size={18} className="text-terracotta" />
            <div><div className="text-lg font-bold text-slate-brand leading-none">{existing.view_count ?? existing.views ?? 0}</div><div className="text-xs text-stone-500">profile views</div></div>
          </div>
        )}
      </div>

      {existing && (
        <div className="mt-4 rounded-lg bg-tier1-bg border border-tier1-border text-tier1-text px-4 py-2.5 text-sm" data-testid="status-banner">
          Status: <strong>{existing.status === "published" ? `Published · Tier ${existing.verification_tier}` : "In verification queue"}</strong>. You can keep editing below.
        </div>
      )}

      {/* Profile strength */}
      <div className="mt-6 bg-white rounded-xl border border-stone-200 p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-stone-700">Profile strength</span>
          <span className="text-sm font-bold text-terracotta" data-testid="profile-strength">{strength}%</span>
        </div>
        <Progress value={strength} className="h-2" />
      </div>

      {/* Wizard steps */}
      <div className="flex flex-wrap gap-2 mt-6">
        {STEPS.map((s, i) => (
          <button key={s} onClick={() => setStep(i)} data-testid={`wizard-step-${i}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${step === i ? "bg-terracotta text-white border-terracotta" : "bg-white text-stone-600 border-stone-200"}`}>
            {i + 1}. {s}
          </button>
        ))}
      </div>

      <div className="mt-5 bg-white rounded-xl border border-stone-200 p-6 space-y-4">
        {step === 0 && (
          <>
            <Row label="Legal name"><Input value={form.legal_name} onChange={(e) => set("legal_name", e.target.value)} data-testid="wiz-legal-name" /></Row>
            <Row label="Display name"><Input value={form.display_name} onChange={(e) => set("display_name", e.target.value)} data-testid="wiz-display-name" /></Row>
            <Row label="Mission (English)"><Textarea value={form.mission} onChange={(e) => set("mission", e.target.value)} data-testid="wiz-mission" /></Row>
            <Row label="Mission (Tamil)"><Textarea value={form.mission_ta} onChange={(e) => set("mission_ta", e.target.value)} className="font-tamil" data-testid="wiz-mission-ta" /></Row>
            <Row label="Hero image"><MediaUploader kind="hero" value={form.hero_image} onChange={(v) => set("hero_image", v)} /></Row>
            <Row label="Photo gallery (site photos of work in progress)"><MediaUploader kind="gallery" multiple value={form.gallery || []} onChange={(v) => set("gallery", v)} /></Row>
          </>
        )}
        {step === 1 && (
          <>
            <Row label="Registration type">
              <select value={form.reg_type} onChange={(e) => set("reg_type", e.target.value)} className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm" data-testid="wiz-reg-type">
                {REG_TYPES.map((r) => <option key={r}>{r}</option>)}
              </select>
            </Row>
            <Row label="Date of incorporation"><Input type="date" value={form.incorporation_date} onChange={(e) => set("incorporation_date", e.target.value)} data-testid="wiz-incorp" /></Row>
            <Row label="DARPAN ID"><Input value={form.darpan_id} onChange={(e) => set("darpan_id", e.target.value)} data-testid="wiz-darpan" /></Row>
            <Row label="District">
              <select value={form.district_id} onChange={(e) => set("district_id", e.target.value)} className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm" data-testid="wiz-district">
                {DISTRICTS.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </Row>
            <Row label="Pincode"><Input value={form.pincode} onChange={(e) => set("pincode", e.target.value)} data-testid="wiz-pincode" /></Row>
          </>
        )}
        {step === 2 && (
          <>
            <p className="text-sm text-stone-500">Add your registrations. Numbers are cross-checked during verification. New profiles start at Tier 0 (self-declared).</p>
            {form.credentials.map((c, i) => (
              <div key={i} className="flex flex-wrap items-end gap-2 border-b border-stone-100 pb-3">
                <div className="w-32"><Label className="text-xs">Type</Label>
                  <select value={c.type} onChange={(e) => setCred(i, "type", e.target.value)} className="w-full rounded-lg border border-stone-300 px-2 py-2 text-sm" data-testid={`wiz-cred-type-${i}`}>
                    {CREDENTIAL_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                </div>
                <div className="flex-1 min-w-[140px]"><Label className="text-xs">Number</Label><Input value={c.number} onChange={(e) => setCred(i, "number", e.target.value)} data-testid={`wiz-cred-num-${i}`} /></div>
                <div className="w-40"><Label className="text-xs">Expiry (if any)</Label><Input type="date" value={c.expiry_date} onChange={(e) => setCred(i, "expiry_date", e.target.value)} data-testid={`wiz-cred-exp-${i}`} /></div>
                <Button variant="ghost" size="icon" onClick={() => rmCred(i)} data-testid={`wiz-cred-remove-${i}`}><Trash2 size={16} className="text-red-500" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addCred} data-testid="wiz-add-cred"><Plus size={15} className="mr-1" /> Add credential</Button>
          </>
        )}
        {step === 3 && (
          <>
            <Label className="text-sm">Causes (drives both human tags and Schedule VII codes)</Label>
            <div className="flex flex-wrap gap-2">
              {CAUSES.map((c) => (
                <button key={c.id} onClick={() => toggleCause(c.id)} data-testid={`wiz-cause-${c.id}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border ${form.human_cause_tags.includes(c.id) ? "bg-terracotta text-white border-terracotta" : "bg-white text-stone-600 border-stone-200"}`}>
                  {c.name}
                </button>
              ))}
            </div>
            <Row label="Beneficiaries reached"><Input type="number" value={form.beneficiary_count} onChange={(e) => set("beneficiary_count", parseInt(e.target.value || "0"))} data-testid="wiz-beneficiaries" /></Row>
            <Row label="Budget band">
              <select value={form.budget_band} onChange={(e) => set("budget_band", e.target.value)} className="w-full rounded-lg border border-stone-300 px-3 py-2 text-sm" data-testid="wiz-budget">
                {BUDGET_BANDS.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
              </select>
            </Row>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.accepts_volunteers} onChange={(e) => set("accepts_volunteers", e.target.checked)} data-testid="wiz-volunteers" /> We accept volunteers</label>
            <div className="pt-2">
              <div className="flex items-center justify-between mb-1"><Label className="text-sm">Story / project write-up</Label>
                <Button variant="ghost" size="sm" onClick={aiStory} disabled={aiLoading} className="text-terracotta" data-testid="wiz-ai-story">
                  {aiLoading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Sparkles size={14} className="mr-1" />} AI draft
                </Button>
              </div>
              <Textarea value={form.story} onChange={(e) => set("story", e.target.value)} rows={5} data-testid="wiz-story" />
            </div>
          </>
        )}
        {step === 4 && (
          <>
            <Row label="Contact person"><Input value={form.contact.person} onChange={(e) => setContact("person", e.target.value)} data-testid="wiz-contact-person" /></Row>
            <Row label="Phone"><Input value={form.contact.phone} onChange={(e) => setContact("phone", e.target.value)} data-testid="wiz-contact-phone" /></Row>
            <Row label="Email"><Input value={form.contact.email} onChange={(e) => setContact("email", e.target.value)} data-testid="wiz-contact-email" /></Row>
            <Row label="Visiting hours"><Input value={form.contact.visiting_hours} onChange={(e) => setContact("visiting_hours", e.target.value)} data-testid="wiz-visiting" /></Row>
            <Row label="Address"><Input value={form.contact.address} onChange={(e) => setContact("address", e.target.value)} data-testid="wiz-address" /></Row>
            <Row label="Your own donation page URL (opens off-platform)"><Input value={form.contact.donation_url} onChange={(e) => setContact("donation_url", e.target.value)} placeholder="https://…" data-testid="wiz-donation-url" /></Row>
          </>
        )}
        {step === 5 && (
          <div className="space-y-2 text-sm">
            <h3 className="font-bold text-slate-brand text-lg">Review & submit</h3>
            <p><strong>{form.display_name || "—"}</strong> · {form.reg_type} · {DISTRICTS.find((d) => d.id === form.district_id)?.name}</p>
            <p>Causes: {form.human_cause_tags.join(", ") || "none"}</p>
            <p>Credentials: {form.credentials.filter((c) => c.number).map((c) => c.type).join(", ") || "none"}</p>
            <p className="text-stone-500">On submit, your profile enters the verification queue at Tier 0. Our team reviews documents and assigns a higher tier.</p>
            <Button onClick={submit} disabled={saving || !form.display_name} className="mt-3 bg-terracotta hover:bg-terracotta-hover" data-testid="wiz-submit">
              {saving ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : <Check size={16} className="mr-1.5" />} Submit for verification
            </Button>
          </div>
        )}

        <div className="flex justify-between pt-4 border-t border-stone-100">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="wiz-prev">Back</Button>
          {step < STEPS.length - 1 && <Button variant="outline" onClick={() => setStep(step + 1)} data-testid="wiz-next">Next</Button>}
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return <div><Label className="text-sm mb-1.5 block">{label}</Label>{children}</div>;
}
